import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Divider,
  Skeleton,
  Typography,
  Box,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { useAuth } from "../../../auth/useAuth";
import { useNavigate, useParams } from "react-router-dom";

import { APP_API_V1_BASE } from "../../../config/api";
const Row = ({ label, value, mono }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    gap={2}
    py={1.2}
  >
    <Typography
      fontSize={13}
      color="text.secondary"
      sx={{ whiteSpace: "nowrap", pt: "2px" }}
    >
      {label}
    </Typography>
    <Typography
      fontSize={13}
      fontWeight={500}
      textAlign="right"
      sx={{
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all",
        maxWidth: "60%",
        color: value ? "text.primary" : "text.disabled",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export default function FirmwarePackageDetails({
  open,
  onClose,
  id,
  stateModel,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const resolvedId = id || params.id;
  const isDialogMode = typeof open === "boolean";
  const isOpen = isDialogMode ? open : Boolean(resolvedId);
  const handleClose = onClose || (() => navigate("/ota/packages"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localSasToken, setLocalSasToken] = useState(null);
  const [localSasExpiry, setLocalSasExpiry] = useState(null);
  const [downloadAccessLoading, setDownloadAccessLoading] = useState(false);

  const sasToken = stateModel?.sasToken || localSasToken;
  const sasTokenExpiry = stateModel?.expiresAt || localSasExpiry;
  // Proxy-only frontend gating is temporarily disabled.
  // const proxyOnlyMode = Boolean(
  //   stateModel?.downloadPolicy?.required || downloadPolicy?.required,
  // );
  const proxyOnlyMode = false;
  const token = localStorage.getItem("authToken");
  const { activeOrg } = useAuth();

  useEffect(() => {
    if (!resolvedId || !isOpen) return;
    setLoading(true);
    setData(null);
    axios

      .get(`${APP_API_V1_BASE}/firmware/packages/${resolvedId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      })
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [resolvedId, token, isOpen, activeOrg?.id]);

  useEffect(() => {
    if (!isOpen || !token) return;
    if (stateModel?.sasToken || stateModel?.expiresAt) return;

    let mounted = true;
    const fetchDownloadAccess = async () => {
      try {
        setDownloadAccessLoading(true);
        const res = await axios.get(
          `${APP_API_V1_BASE}/firmware/packages/storage/sas-token`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
            },
          },
        );
        if (!mounted) return;
        // Proxy policy handling is intentionally disabled for now on frontend.
        setLocalSasToken(res.data?.sas_token || null);
        setLocalSasExpiry(res.data?.expires_at || null);
      } catch (error) {
        if (!mounted) return;
        setLocalSasToken(null);
        setLocalSasExpiry(null);
      } finally {
        if (mounted) setDownloadAccessLoading(false);
      }
    };

    fetchDownloadAccess();
    return () => {
      mounted = false;
    };
  }, [isOpen, token, activeOrg?.id, stateModel?.sasToken, stateModel?.expiresAt]);

  const handleDownload = () => {
    if (!sasToken) return alert("Download token missing or expired");
    if (sasTokenExpiry && new Date() > new Date(sasTokenExpiry))
      return alert("Download token has expired");
    window.open(`${data.file.url}?${sasToken}`, "_blank");
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography fontWeight={700} fontSize={17} mb={0.5}>
              Firmware Package Details
            </Typography>
            {loading ? (
              <Skeleton width={120} height={20} />
            ) : (
              <Typography fontSize={13} color="text.secondary">
                {data?.name}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {data?.version && (
              <Chip label={`v${data.version}`} size="small" color="primary" />
            )}
            {data?.is_latest && (
              <Chip label="Latest" size="small" color="success" />
            )}
            {/* Proxy-only indicator intentionally hidden for now */}
            {!proxyOnlyMode && sasTokenExpiry && (
              <Chip
                label={`Token: ${new Date(sasTokenExpiry).toLocaleTimeString()}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Divider />

        {/* Body */}
        <Box px={3} py={1}>
          {loading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <Box key={i} py={1.2} borderBottom="1px solid #f1f5f9">
                <Skeleton width="100%" height={20} />
              </Box>
            ))
          ) : data ? (
            <>
              <Row label="Name" value={data.name} />
              <Divider light />
              <Row label="Version" value={data.version} />
              <Divider light />
              <Row
                label="Device Type"
                value={data.device_type?.name || data.device_type_id}
              />
              <Divider light />
              <Row label="File Size" value={data.file?.size_human} />
              <Divider light />
              <Row label="SHA256 Checksum" value={data.file?.checksum} mono />
              <Divider light />
              <Row label="Release Notes" value={data.release_notes} />
              {downloadAccessLoading && (
                <>
                  <Divider light />
                  <Row label="Download Access" value="Resolving token..." />
                </>
              )}
            </>
          ) : null}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Close
        </Button>
        {data?.permissions?.can_download && (
          <Button
            variant="contained"
            startIcon={<Download fontSize="small" />}
            onClick={handleDownload}
            disabled={downloadAccessLoading}
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
